import type { V2 } from '../core/math';

/**
 * Pure 2D geometry helpers for layout generation (XZ plane). No three.js, no allocation concerns —
 * this runs once at load.
 */

export interface Obb {
  center: V2;
  halfX: number;
  halfZ: number;
  /** Heading convention: local +Z = forward(angle) = (sin a, cos a); local +X = (cos a, -sin a). */
  angle: number;
}

export function obbAxes(angle: number): { ax: V2; az: V2 } {
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  return { ax: { x: c, z: -s }, az: { x: s, z: c } };
}

export function obbCorners(b: Obb): V2[] {
  const { ax, az } = obbAxes(b.angle);
  const out: V2[] = [];
  for (const [sx, sz] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ] as const) {
    out.push({
      x: b.center.x + ax.x * b.halfX * sx + az.x * b.halfZ * sz,
      z: b.center.z + ax.z * b.halfX * sx + az.z * b.halfZ * sz,
    });
  }
  return out;
}

/** Local -> world for an OBB frame. */
export function obbPoint(b: { center: V2; angle: number }, lx: number, lz: number): V2 {
  const { ax, az } = obbAxes(b.angle);
  return { x: b.center.x + ax.x * lx + az.x * lz, z: b.center.z + ax.z * lx + az.z * lz };
}

function project(poly: V2[], axis: V2): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of poly) {
    const d = p.x * axis.x + p.z * axis.z;
    if (d < lo) lo = d;
    if (d > hi) hi = d;
  }
  return [lo, hi];
}

/** SAT overlap test for convex polygons; `margin` > 0 requires that much penetration to count. */
export function convexOverlap(a: V2[], b: V2[], margin = 0): boolean {
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i]!;
      const q = poly[(i + 1) % poly.length]!;
      const ex = q.x - p.x;
      const ez = q.z - p.z;
      const l = Math.hypot(ex, ez) || 1;
      const axis = { x: -ez / l, z: ex / l };
      const [a0, a1] = project(a, axis);
      const [b0, b1] = project(b, axis);
      if (a1 - margin <= b0 || b1 - margin <= a0) return false;
    }
  }
  return true;
}

export function obbOverlap(a: Obb, b: Obb, margin = 0): boolean {
  return convexOverlap(obbCorners(a), obbCorners(b), margin);
}

export function pointSegmentDistance(p: V2, a: V2, b: V2): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const l2 = abx * abx + abz * abz;
  let t = l2 > 0 ? ((p.x - a.x) * abx + (p.z - a.z) * abz) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + abx * t), p.z - (a.z + abz * t));
}

function segmentsIntersect(p1: V2, p2: V2, q1: V2, q2: V2): boolean {
  const d = (a: V2, b: V2, c: V2): number => (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
  const d1 = d(q1, q2, p1);
  const d2 = d(q1, q2, p2);
  const d3 = d(p1, p2, q1);
  const d4 = d(p1, p2, q2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function pointInPolygon(p: V2, poly: V2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** Minimum distance between a (closed) polygon and a segment; 0 when they touch/overlap. */
export function polygonSegmentDistance(poly: V2[], a: V2, b: V2): number {
  if (pointInPolygon(a, poly) || pointInPolygon(b, poly)) return 0;
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    if (segmentsIntersect(p, q, a, b)) return 0;
    best = Math.min(best, pointSegmentDistance(p, a, b), pointSegmentDistance(a, p, q), pointSegmentDistance(b, p, q));
  }
  return best;
}

export function polygonArea(poly: V2[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    s += a.x * b.z - b.x * a.z;
  }
  return s / 2;
}

export function polygonCentroid(poly: V2[]): V2 {
  let cx = 0;
  let cz = 0;
  for (const p of poly) {
    cx += p.x;
    cz += p.z;
  }
  return { x: cx / poly.length, z: cz / poly.length };
}

function lineIntersect(p: V2, d: V2, q: V2, e: V2): V2 | null {
  const den = d.x * e.z - d.z * e.x;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((q.x - p.x) * e.z - (q.z - p.z) * e.x) / den;
  return { x: p.x + d.x * t, z: p.z + d.z * t };
}

/**
 * Offsets a closed polyline (the boundary of a face, traversed so the face lies on the `side` of each
 * directed edge: +1 = left, -1 = right) INTO the face. Each edge has its own offset distance.
 * Convex corners get a fillet of radius `radius[i]` (corner at vertex i), reflex corners wrap round
 * the vertex with an arc. Works for the mostly-orthogonal street networks we author.
 */
export function offsetFace(points: V2[], dist: number[], radius: number[], side: 1 | -1, arcSegs = 6): V2[] {
  const n = points.length;
  const out: V2[] = [];
  const dirs: V2[] = [];
  const norms: V2[] = [];
  for (let i = 0; i < n; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    const l = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const d = { x: (b.x - a.x) / l, z: (b.z - a.z) / l };
    dirs.push(d);
    // left normal of d in (x,z) with the y-up/heading convention used by roads.ts: left = (d.z, -d.x)
    const left = { x: d.z, z: -d.x };
    norms.push({ x: left.x * side, z: left.z * side });
  }
  // corner i sits at points[i] between edge i-1 (incoming) and edge i (outgoing)
  for (let i = 0; i < n; i++) {
    const ip = (i - 1 + n) % n;
    const v = points[i]!;
    const d0 = dirs[ip]!;
    const d1 = dirs[i]!;
    const n0 = norms[ip]!;
    const n1 = norms[i]!;
    const o0 = dist[ip]!;
    const o1 = dist[i]!;
    const cross = d0.x * d1.z - d0.z * d1.x;
    const dotp = d0.x * d1.x + d0.z * d1.z;
    // Turning towards the face side = convex corner of the face.
    // With left = (d.z, -d.x), a turn towards the left has cross < 0.
    const turn = -cross * side; // > 0: turns into the face (convex)
    if (Math.abs(cross) < 0.02 && dotp > 0) {
      out.push({ x: v.x + n0.x * o0, z: v.z + n0.z * o0 });
      if (Math.abs(o0 - o1) > 0.01) out.push({ x: v.x + n1.x * o1, z: v.z + n1.z * o1 });
      continue;
    }
    if (turn > 0) {
      const r = radius[i] ?? 0;
      const p0 = { x: v.x + n0.x * (o0 + r), z: v.z + n0.z * (o0 + r) };
      const p1 = { x: v.x + n1.x * (o1 + r), z: v.z + n1.z * (o1 + r) };
      const c = lineIntersect(p0, d0, p1, d1);
      if (!c) {
        out.push({ x: v.x + n0.x * o0, z: v.z + n0.z * o0 });
        continue;
      }
      if (r <= 0.05) {
        out.push(c);
        continue;
      }
      const t0 = { x: c.x - n0.x * r, z: c.z - n0.z * r };
      const t1 = { x: c.x - n1.x * r, z: c.z - n1.z * r };
      const a0 = Math.atan2(t0.z - c.z, t0.x - c.x);
      let a1 = Math.atan2(t1.z - c.z, t1.x - c.x);
      let da = a1 - a0;
      while (da > Math.PI) da -= Math.PI * 2;
      while (da < -Math.PI) da += Math.PI * 2;
      a1 = a0 + da;
      for (let s = 0; s <= arcSegs; s++) {
        const a = a0 + (da * s) / arcSegs;
        out.push({ x: c.x + Math.cos(a) * r, z: c.z + Math.sin(a) * r });
      }
    } else {
      // Reflex corner: wrap round the vertex.
      const a0 = Math.atan2(n0.z, n0.x);
      const a1 = Math.atan2(n1.z, n1.x);
      let da = a1 - a0;
      while (da > Math.PI) da -= Math.PI * 2;
      while (da < -Math.PI) da += Math.PI * 2;
      const segs = Math.max(2, Math.ceil((Math.abs(da) / (Math.PI / 2)) * arcSegs));
      for (let s = 0; s <= segs; s++) {
        const t = s / segs;
        const a = a0 + da * t;
        const r = o0 + (o1 - o0) * t;
        out.push({ x: v.x + Math.cos(a) * r, z: v.z + Math.sin(a) * r });
      }
    }
  }
  return dedupe(out);
}

function dedupe(pts: V2[]): V2[] {
  const out: V2[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (q && Math.hypot(p.x - q.x, p.z - q.z) < 0.02) continue;
    out.push(p);
  }
  if (out.length > 2) {
    const f = out[0]!;
    const l = out[out.length - 1]!;
    if (Math.hypot(f.x - l.x, f.z - l.z) < 0.02) out.pop();
  }
  return out;
}

/** Resample a closed polygon so no edge is longer than `maxLen` (for ped paths). */
export function resampleClosed(poly: V2[], maxLen: number): V2[] {
  const out: V2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    const l = Math.hypot(b.x - a.x, b.z - a.z);
    const n = Math.max(1, Math.ceil(l / maxLen));
    for (let k = 0; k < n; k++) out.push({ x: a.x + ((b.x - a.x) * k) / n, z: a.z + ((b.z - a.z) * k) / n });
  }
  return out;
}

/** Simplify near-collinear points (keeps corners). */
export function simplifyClosed(poly: V2[], tol = 0.05): V2[] {
  if (poly.length < 4) return poly;
  // Sequential: a point is dropped only if it lies on the segment from the last KEPT point to the next.
  const n = poly.length;
  const out: V2[] = [poly[0]!];
  for (let i = 1; i < n; i++) {
    const a = out[out.length - 1]!;
    const b = poly[i]!;
    const c = poly[(i + 1) % n]!;
    if (pointSegmentDistance(b, a, c) > tol) out.push(b);
  }
  return out.length >= 3 ? out : poly;
}
